import type {
  RuleDecision,
  StickerProductV1,
  StickerShapeV1,
} from '../../contracts/src/index.ts';

export const STICKER_PRESETS = [
  { group: 'Hojalata', code: '1ahn', lid: '45 mm', shape: 'circle', widthMm: 42, heightMm: 42 },
  { group: 'Hojalata', code: '2ahn', lid: '60 mm', shape: 'circle', widthMm: 51, heightMm: 51 },
  { group: 'Hojalata', code: '83hn', lid: '83 mm', shape: 'circle', widthMm: 58, heightMm: 58 },
  { group: 'Hojalata', code: '92hn', lid: '92 mm', shape: 'circle', widthMm: 68, heightMm: 68 },
  { group: 'Hojalata', code: '100bh', lid: '100 mm', shape: 'circle', widthMm: 65, heightMm: 65 },
  { group: 'Hojalata', code: '120hn', lid: '120 mm', shape: 'circle', widthMm: 88, heightMm: 88 },
  { group: 'Hojalata', code: '158bh', lid: '158 mm', shape: 'circle', widthMm: 140, heightMm: 140 },
  { group: 'ABS', code: '1ap', lid: '45 mm', shape: 'circle', widthMm: 50, heightMm: 50 },
  { group: 'ABS', code: '2ap', lid: '60 mm', shape: 'circle', widthMm: 65, heightMm: 65 },
  { group: 'ABS', code: '83ap', lid: '83 mm', shape: 'circle', widthMm: 86, heightMm: 86 },
  { group: 'ABS', code: '92ap', lid: '92 mm', shape: 'circle', widthMm: 60, heightMm: 60 },
  { group: 'ABS', code: '120ap', lid: '120 mm', shape: 'circle', widthMm: 85, heightMm: 85 },
  { group: 'Plásticas', code: '1A', lid: '45 mm', shape: 'circle', widthMm: 45, heightMm: 45 },
  { group: 'Plásticas', code: '2A', lid: '60 mm', shape: 'circle', widthMm: 57, heightMm: 57 },
  { group: 'Plásticas', code: '3A', lid: '80 mm', shape: 'circle', widthMm: 74, heightMm: 74 },
  { group: 'Plásticas', code: '4B', lid: '100 mm', shape: 'circle', widthMm: 99, heightMm: 99 },
  { group: 'Plásticas', code: '5B', lid: '125 mm', shape: 'circle', widthMm: 124, heightMm: 124 },
  { group: 'Plásticas', code: '6B', lid: '150 mm', shape: 'circle', widthMm: 152, heightMm: 152 },
] as const satisfies readonly {
  readonly group: string;
  readonly code: string;
  readonly lid: string;
  readonly shape: 'circle';
  readonly widthMm: number;
  readonly heightMm: number;
}[];

export type StickerPreset = (typeof STICKER_PRESETS)[number];
export type StickerPresetCode = StickerPreset['code'];
export type StickerGroup = StickerPreset['group'];

export const DEFAULT_STICKER_PRESET_CODE: StickerPresetCode = '83hn';

export interface StickerCommercialLimits {
  readonly minimumWidthMm: number;
  readonly maximumWidthMm: number;
  readonly minimumHeightMm: number;
  readonly maximumHeightMm: number;
}

export const CUSTOM_STICKER_COMMERCIAL_LIMITS = {
  status: 'pending',
  value: null,
  source:
    'V4.6 index.html:2357-2409; flujo vNext seccion 11 punto 5; auditoria 12 R11',
  reason:
    'V4.6 acepta cualquier medida finita mayor que cero. No hay minimos ni maximos ' +
    'comerciales confirmados para circulo, cuadrado o rectangulo.',
} as const satisfies RuleDecision<StickerCommercialLimits>;

export type StickerMeasurementErrorCode = 'required' | 'format' | 'non-positive';

export type StickerMeasurementResult =
  | {
      readonly ok: true;
      readonly centimeters: number;
      readonly millimeters: number;
    }
  | {
      readonly ok: false;
      readonly code: StickerMeasurementErrorCode;
      readonly message: string;
    };

export function parseStickerMeasurementCm(
  input: string | number | null | undefined,
): StickerMeasurementResult {
  const normalized = String(input ?? '').replace(',', '.').trim();
  if (normalized.length === 0) {
    return { ok: false, code: 'required', message: 'Ingresa una medida.' };
  }

  const centimeters = Number(normalized);
  if (!Number.isFinite(centimeters)) {
    return {
      ok: false,
      code: 'format',
      message: 'Usa un numero valido, con coma o punto decimal.',
    };
  }
  if (centimeters <= 0) {
    return {
      ok: false,
      code: 'non-positive',
      message: 'La medida debe ser mayor que cero.',
    };
  }

  const millimeters = Math.round(centimeters * 10);
  if (millimeters <= 0) {
    return {
      ok: false,
      code: 'non-positive',
      message: 'La medida debe producir al menos 1 mm fisico.',
    };
  }

  return { ok: true, centimeters, millimeters };
}

export function formatStickerCmFromMm(millimeters: number): string {
  const centimeters = millimeters / 10;
  return (Math.round(centimeters * 10) / 10).toString().replace('.', ',');
}

export function findStickerPreset(code: string): StickerPreset | undefined {
  return STICKER_PRESETS.find((preset) => preset.code === code);
}

export function getStickerPreset(code: string): StickerPreset {
  const preset = findStickerPreset(code);
  if (!preset) {
    throw new Error('Preset de Sticker desconocido: ' + code);
  }
  return preset;
}

export function createPresetStickerProductV1(preset: StickerPreset): StickerProductV1 {
  return {
    kind: 'sticker',
    source: {
      kind: 'preset',
      presetCode: preset.code,
      group: preset.group,
    },
    shape: preset.shape,
    widthMm: preset.widthMm,
    heightMm: preset.heightMm,
  };
}

export type CustomStickerProductResult =
  | {
      readonly ok: true;
      readonly value: StickerProductV1 & {
        readonly source: {
          readonly kind: 'custom';
          readonly commercialLimitsStatus: 'pending';
        };
      };
      readonly notices: readonly [
        {
          readonly code: 'commercial-limits-pending';
          readonly message: string;
        },
      ];
    }
  | {
      readonly ok: false;
      readonly field: 'width' | 'height';
      readonly error: StickerMeasurementResult & { readonly ok: false };
    };

export function createCustomStickerProductV1(input: {
  readonly shape: StickerShapeV1;
  readonly widthInput: string | number;
  readonly heightInput?: string | number;
}): CustomStickerProductResult {
  const width = parseStickerMeasurementCm(input.widthInput);
  if (!width.ok) {
    return { ok: false, field: 'width', error: width };
  }

  const height =
    input.shape === 'rect'
      ? parseStickerMeasurementCm(input.heightInput)
      : width;
  if (!height.ok) {
    return { ok: false, field: 'height', error: height };
  }

  return {
    ok: true,
    value: {
      kind: 'sticker',
      source: {
        kind: 'custom',
        commercialLimitsStatus: 'pending',
      },
      shape: input.shape,
      widthMm: width.millimeters,
      heightMm: height.millimeters,
    },
    notices: [
      {
        code: 'commercial-limits-pending',
        message: CUSTOM_STICKER_COMMERCIAL_LIMITS.reason,
      },
    ],
  };
}
